import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Cache object to store comments
const commentsCache = {};

// Cache time in milliseconds (30 seconds)
const CACHE_EXPIRATION = 30 * 1000;

// Store timestamps of recent API calls to implement basic rate limiting
const requestTimestamps = {
  addComment: []
};

// Function to check if we should throttle a request
const shouldThrottle = (endpoint, rateLimit = 3, timeWindow = 60000) => {
  const now = Date.now();
  
  // Remove timestamps older than the time window
  requestTimestamps[endpoint] = requestTimestamps[endpoint].filter(
    timestamp => now - timestamp < timeWindow
  );
  
  // If we have fewer requests than the rate limit, allow this one
  if (requestTimestamps[endpoint].length < rateLimit) {
    requestTimestamps[endpoint].push(now);
    return false;
  }
  
  // Otherwise, throttle
  return true;
};

const commentsService = {
  getComments: async (messageId, page = 1, limit = 10) => {
    try {
      // Generate cache key based on parameters
      const cacheKey = `comments:${messageId}:${page}:${limit}`;
      
      // Check if we have a valid cache entry
      if (commentsCache[cacheKey] && 
          commentsCache[cacheKey].timestamp > Date.now() - CACHE_EXPIRATION) {
        return commentsCache[cacheKey].data;
      }
      
      const response = await axios.get(`${API_URL}/api/messages/${messageId}/comments`, {
        params: { page, limit }
      });
      
      // Store in cache
      commentsCache[cacheKey] = {
        data: response.data,
        timestamp: Date.now()
      };
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener comentarios:', error);
      throw error;
    }
  },
  
  addComment: async (messageId, text, parentId = null) => {
    const token = localStorage.getItem('authToken');
    
    try {
      // Apply client-side rate limiting (8 comments per minute - increased from 3)
      if (shouldThrottle('addComment', 8, 60000)) {
        throw new Error('Estás comentando demasiado rápido. Por favor, espera un momento antes de publicar otro comentario.');
      }
      
      // Check if messageId is valid
      if (!messageId || typeof messageId !== 'string' || messageId.length < 5) {
        console.error('Invalid message ID:', messageId);
        throw new Error('Invalid message ID');
      }
      
      // Get user data from local storage
      const userString = localStorage.getItem('user');
      if (!userString) {
        throw new Error('User information not found. Please log in again.');
      }
      
      const user = JSON.parse(userString);
      
      const response = await axios.post(
        `${API_URL}/api/messages/${messageId}/comments`, 
        { 
          text, 
          parent_id: parentId,
          user_id: user.id,
          username: user.username
        }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Invalidate cache for this message's comments
      commentsService.invalidateMessageComments(messageId);
      
      // Invalidate user cache for the message author
      try {
        // First check if response contains the message owner's username
        if (response.data && response.data.message_owner) {
          // Try to import usersService without creating circular dependencies
          const { default: usersService } = await import('./users');
          // Invalidate the message owner's profile
          usersService.handleMessageCommented(messageId, user.id, response.data.message_owner);
        } else {
          // If the message owner is not in the response, try to get it from the message
          try {
            const { default: messagesService } = await import('./messages');
            const message = await messagesService.getMessage(messageId);
            if (message && message.username) {
              const { default: usersService } = await import('./users');
              usersService.handleMessageCommented(messageId, user.id, message.username);
            }
          } catch (err) {
            console.warn('Could not get message details:', err);
          }
        }
      } catch (err) {
        console.warn('Could not invalidate user cache:', err);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error adding comment:', error);
      
      // If it's our throttling error, return it directly
      if (error.message === 'Estás comentando demasiado rápido. Por favor, espera un momento antes de publicar otro comentario.') {
        throw error;
      }
      
      // For rate limit responses from the server
      if (error.response && error.response.status === 429) {
        throw new Error(error.response.data || 'Has alcanzado el límite de comentarios. Inténtalo más tarde.');
      }
      
      throw error;
    }
  },
  
  // Cache invalidation methods
  invalidateMessageComments: (messageId) => {
    // Remove all cache entries for this message
    const prefix = `comments:${messageId}:`;
    commentsService.clearByPrefix(prefix);
  },
  
  clearByPrefix: (prefix) => {
    Object.keys(commentsCache).forEach(key => {
      if (key.startsWith(prefix)) {
        delete commentsCache[key];
      }
    });
  },
  
  invalidateCommentsCache: () => {
    // Clear all cache
    Object.keys(commentsCache).forEach(key => delete commentsCache[key]);
  }
};

export default commentsService;